// =====================================================================================
// FILE: src/hooks/useAssetCache.js (FIXED - ALL ASSET FIELDS INCLUDED)
// =====================================================================================
import { useState, useCallback } from "react";
import { fetchAssetsByIds } from "../airtable";

export function useAssetCache() {
  const [assetCache, setAssetCache] = useState(new Map());

  const warmCache = useCallback(async (items) => {
    // ✅ ALWAYS fetch ALL assets, not just the ones currently in use
    // This ensures the asset picker is populated even for empty rows
    try {
      const { fetchAllAssets } = await import("../airtable");
      const allAssets = await fetchAllAssets();
      setAssetCache(allAssets);
    } catch (err) {
      console.warn("Failed to fetch all assets, falling back to item-based caching:", err);
      
      // ✅ Fallback: Only cache assets that are currently in use
      const ids = [];
      items.forEach((it) => {
        ids.push(
          it.iptvGfxId,
          it.bigGfxVideosId,
          it.cornerGfxVideosId,
          it.ledAllTypesId,
          it.bigTvcId,
          it.mcgTiledLedId,
          it.marvelTiledLedId,
          it.superFullId,
          it.super169Id,
          it.superSideId,
          it.mcgInterchangeId,
          it.marvelInterchangeId,
          it.marvel360Id,
          it.mcg360Id
        );
      });

      const uniqIds = Array.from(new Set(ids.filter(Boolean)));
      if (!uniqIds.length) return;

      try {
        const map = await fetchAssetsByIds(uniqIds);
        setAssetCache((prev) => {
          const next = new Map(prev);
          map.forEach((v, k) => next.set(k, v));
          return next;
        });
      } catch {
        // ignore
      }
    }
  }, []);

  const updateCache = useCallback((newAssetsMap) => {
    setAssetCache((prev) => {
      const next = new Map(prev);
      if (newAssetsMap instanceof Map) {
        newAssetsMap.forEach((v, k) => next.set(k, v));
      }
      return next;
    });
  }, []);

  const getAsset = useCallback(
    (id) => {
      if (!id) return null;
      return assetCache.get(id) || { id, title: id, destination: "", thumb: "" };
    },
    [assetCache]
  );

  return { assetCache, warmCache, updateCache, getAsset };
}
